import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { CasefieldDef, CasefieldWerte } from "./casefieldValues";

// #303 (UC-2, Konzept §8): der gemeinsame Formular-Baustein für Vorgangs-Felder.
// Kontrolliert (Werte kommen von aussen) — die Masken darüber entscheiden, wohin sie gehen.

const auswahlCls =
  "h-9 w-full rounded-md border border-border bg-surface px-2 text-[13px] text-text outline-none focus:border-accent-border disabled:opacity-60";

function Feld({
  feld,
  wert,
  onChange,
  disabled,
}: {
  feld: CasefieldDef;
  wert: string;
  onChange: (v: string) => void;
  disabled: boolean;
}) {
  const id = `cf-${feld.key}`;
  if (feld.type === "choice" && feld.choices && feld.choices.length > 0) {
    return (
      <select
        id={id}
        className={auswahlCls}
        value={wert}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`casefield-${feld.key}`}
      >
        <option value="">—</option>
        {feld.choices.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>
    );
  }
  if (feld.type === "boolean") {
    // Bewusst eine Auswahl statt einer Checkbox: eine Checkbox kann „noch nicht gesetzt"
    // nicht ausdrücken, und genau das ist bei einem optionalen Feld die Wahrheit.
    return (
      <select
        id={id}
        className={auswahlCls}
        value={wert}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`casefield-${feld.key}`}
      >
        <option value="">—</option>
        <option value="true">Ja</option>
        <option value="false">Nein</option>
      </select>
    );
  }
  if (feld.type === "text") {
    return (
      <Textarea
        id={id}
        value={wert}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`casefield-${feld.key}`}
      />
    );
  }
  return (
    <Input
      id={id}
      type={feld.type === "date" ? "date" : "text"}
      inputMode={feld.type === "number" || feld.type === "money" ? "decimal" : undefined}
      value={wert}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      placeholder={feld.type === "money" ? "Betrag" : undefined}
      data-testid={`casefield-${feld.key}`}
    />
  );
}

export function CasefieldForm({
  felder,
  werte,
  onChange,
  disabled = false,
  testId = "casefield-form",
}: {
  felder: readonly CasefieldDef[];
  werte: CasefieldWerte;
  onChange: (werte: CasefieldWerte) => void;
  disabled?: boolean;
  testId?: string;
}) {
  if (felder.length === 0) return null;
  // Konnte auch nur EIN Feld nicht aufgelöst werden, wird das ANGESAGT — der Master prüft
  // gegen die gepinnte Vorlage, und ein als Text abgeschickter Zahlenwert wird abgelehnt.
  const geraten = felder.some((f) => f.unbekannt);

  return (
    <div className="space-y-3" data-testid={testId}>
      {geraten && (
        <p className="text-[12px] text-warn" data-testid="casefield-unbekannt">
          Die Feld-Beschreibungen dieser Vorlage sind nicht abrufbar — die Eingaben werden als
          Text übergeben und können abgelehnt werden.
        </p>
      )}
      <div className="flex flex-wrap gap-3">
        {felder.map((f) => (
          <div key={f.key} className={f.type === "text" ? "w-full space-y-1.5" : "w-60 space-y-1.5"}>
            <Label htmlFor={`cf-${f.key}`}>
              {f.label}
              {f.required && <span className="ml-0.5 text-danger">*</span>}
            </Label>
            <Feld
              feld={f}
              wert={werte[f.key] ?? ""}
              disabled={disabled}
              onChange={(v) => onChange({ ...werte, [f.key]: v })}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
